import type { Reward } from '@/types/reward';

interface RewardCardProps {
  reward: Reward;
  onEdit: (reward: Reward) => void;
  onToggle: (id: string) => void;
}

export default function RewardCard({ reward, onEdit, onToggle }: RewardCardProps) {
  const active = reward.status === 'active';

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-background-200/90 bg-background-50/95 shadow-sm transition-all duration-300 hover:shadow-xl hover:shadow-foreground-950/5 hover:-translate-y-1 flex flex-col">
      {/* Top subtle highlight stripe */}
      <div
        className={`absolute top-0 left-0 right-0 h-[2.5px] bg-gradient-to-r ${
          active ? 'from-emerald-500 to-teal-500' : 'from-slate-400 to-slate-500'
        } opacity-80 group-hover:opacity-100 transition-opacity z-10`}
      />

      {/* Image container with modern badge overlay */}
      <div className="relative h-44 w-full overflow-hidden bg-background-100">
        <img
          src={reward.image}
          alt={reward.name}
          title={`${reward.name} reward`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />

        <span
          className={`absolute top-3 left-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full backdrop-blur-md shadow-md ${
            active
              ? 'bg-emerald-500/90 text-white border border-emerald-400/30'
              : 'bg-slate-700/85 text-slate-100 border border-slate-600/30'
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              active ? 'bg-white animate-pulse' : 'bg-slate-300'
            }`}
          ></span>
          {active ? 'Active' : 'Disabled'}
        </span>

        <span className="absolute bottom-3 right-3 text-xs font-bold px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-md text-white border border-white/10 shadow-xs">
          {reward.category}
        </span>
      </div>

      {/* Content body */}
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-start justify-between gap-3">
          <h4 className="font-heading font-bold text-foreground-950 text-base leading-snug group-hover:text-primary-600 transition-colors">
            {reward.name}
          </h4>
        </div>

        <p className="mt-1.5 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
          {reward.description}
        </p>

        {/* 3 Metric Pills */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-background-100/70 border border-background-200/80 p-2.5 text-center transition-colors group-hover:bg-background-100">
            <p className="text-[11px] font-medium text-foreground-500">Points Cost</p>
            <p className="mt-0.5 font-heading font-extrabold text-blue-600 text-sm">
              {reward.pointsCost.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl bg-background-100/70 border border-background-200/80 p-2.5 text-center transition-colors group-hover:bg-background-100">
            <p className="text-[11px] font-medium text-foreground-500">Stock</p>
            <p className="mt-0.5 font-heading font-extrabold text-foreground-950 text-sm">
              {reward.stock.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl bg-background-100/70 border border-background-200/80 p-2.5 text-center transition-colors group-hover:bg-background-100">
            <p className="text-[11px] font-medium text-foreground-500">Redeemed</p>
            <p className="mt-0.5 font-heading font-extrabold text-emerald-600 text-sm">
              {reward.redeemed.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Action button controls */}
        <div className="mt-5 pt-4 border-t border-background-200/80 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(reward)}
            className="whitespace-nowrap inline-flex items-center gap-1.5 flex-1 justify-center px-3.5 py-2 rounded-xl bg-background-100/80 border border-background-200/90 text-sm font-semibold text-foreground-700 hover:bg-background-200/80 hover:text-foreground-950 transition-all duration-200 cursor-pointer shadow-2xs"
          >
            <i className="ri-edit-line text-sm"></i> Edit
          </button>
          <button
            type="button"
            onClick={() => onToggle(reward.id)}
            className={`whitespace-nowrap inline-flex items-center gap-1.5 flex-1 justify-center px-3.5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer shadow-2xs ${
              active
                ? 'bg-rose-500/10 text-rose-700 border border-rose-500/20 hover:bg-rose-500/20'
                : 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 hover:bg-emerald-500/20'
            }`}
          >
            <i className={active ? 'ri-pause-line' : 'ri-play-line'}></i>
            {active ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}
